// Mirrors backend rule percentages in urfx2.0-backend/src/lib/config/global.js (tradeCheckOption + violationRuleConfig).
// Keep in lockstep with the backend — these drive the dashboard objectives bar and analytics risk gauges.

export type ChallengeType =
  | "twoPhase"
  | "onePhase"
  | "instantFundingPro"
  | "instantFundingLite"
  | "velocity"
  | "instantFund"; // legacy alias → instantFundingPro

export interface ProgramRules {
  dailyLossLimit: number; // %
  maxDrawDown: number;    // %
  profitTarget: number;   // % (payoutEquityGrowthTarget for funded programs)
  dailyProfitCapPercent: number;
  trailingDrawdownLimitPercent: number;
}

// Soft-breach caps from backend violationRuleConfig.common.softBreaches.
// Same across all challenge types today, so exported as a single constant.
// Two soft-breach violations of the same rule suspend the account.
export const SOFT_BREACH_RULES = {
  maxTradesPerDay: 10,
  maxLotSizePerTrade: 10,
  maxTotalExposureLots: 50,
  minTradeDurationSeconds: 60,
  maxPendingOrders: 20,
} as const;

// Activity rule from backend violationRuleConfig.common.activity.
// Account suspended after this many days without trades.
export const INACTIVITY_SUSPEND_DAYS = 30;

// Lite uses a single combinedDrawdownLimit. The rule engine fails when
// max(dailyDD%, maxDD%) >= 4%, so we surface 4% in both daily and max slots.
const LITE_COMBINED = 4;

export function getProgramRules(
  rawChallengeType: string | null | undefined,
  // Kept for callers: both twoPhase phases share one target today (10%).
  _challengePhase?: number | null,
): ProgramRules {
  const challengeType = normalizeChallengeType(rawChallengeType);

  switch (challengeType) {
    case "twoPhase":
      return {
        dailyLossLimit: 6,
        maxDrawDown: 12,
        profitTarget: 10,
        dailyProfitCapPercent: 4,
        trailingDrawdownLimitPercent: 12,
      };
    case "onePhase":
      return {
        dailyLossLimit: 5,
        maxDrawDown: 10,
        profitTarget: 10,
        dailyProfitCapPercent: 4,
        trailingDrawdownLimitPercent: 10,
      };
    case "instantFundingPro":
      return {
        dailyLossLimit: 6,
        maxDrawDown: 12,
        profitTarget: 10, // payoutEquityGrowthTarget
        dailyProfitCapPercent: 3,
        trailingDrawdownLimitPercent: 12,
      };
    case "instantFundingLite":
      return {
        dailyLossLimit: LITE_COMBINED,
        maxDrawDown: LITE_COMBINED,
        profitTarget: 8, // payoutEquityGrowthTarget
        dailyProfitCapPercent: 3,
        trailingDrawdownLimitPercent: LITE_COMBINED,
      };
    case "velocity":
      // Single-phase evaluation: profit 10%, daily DD 10%, max DD 20%.
      return {
        dailyLossLimit: 10,
        maxDrawDown: 20,
        profitTarget: 10,
        dailyProfitCapPercent: 4,
        trailingDrawdownLimitPercent: 20,
      };
    default:
      // Unknown / null challengeType — fall back to twoPhase phase1 so the UI
      // still renders something sane rather than zeros.
      return {
        dailyLossLimit: 6,
        maxDrawDown: 12,
        profitTarget: 10,
        dailyProfitCapPercent: 4,
        trailingDrawdownLimitPercent: 12,
      };
  }
}

function normalizeChallengeType(raw: string | null | undefined): ChallengeType | null {
  if (!raw) return null;
  if (raw === "instantFund" || raw === "instant") return "instantFundingPro";
  if (
    raw === "twoPhase" ||
    raw === "onePhase" ||
    raw === "instantFundingPro" ||
    raw === "instantFundingLite" ||
    raw === "velocity"
  ) {
    return raw;
  }
  return null;
}
